import { CustomButton, StreamlineIcon } from "@hapinest/theme";
import AddCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/remove-add/add-circle.svg";
import SubtractCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/remove-add/subtract-circle.svg";
import { Box, DialogActions, DialogContent, DialogTitle, IconButton, makeStyles, Switch, Typography } from "@material-ui/core";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useI18N } from "../../../common/common";
import NoIndexDialog from "../../dialog/noIndex/NoIndexDialog";

const useStyles = makeStyles((theme) => ({
    dialog: {
        padding: theme.spacing(1),
        "& .MuiDialogTitle-root": {
            padding: 0
        },
        "& .MuiDialogContent-dividers": {
            padding: theme.spacing(3.5, 5),
            [theme.breakpoints.up("sm")]: {
                padding: theme.spacing(3.5, 13),
            },
        },
        "& .MuiDialogActions-root": {
            padding: theme.spacing(2, 0, 1, 0),
            justifyContent: "center"
        }
    },
    removeAddIcon: {
        fontSize: theme.spacing(3.5),
        color: theme.palette.primary.main,
        "&.disabled > *": {
            color: theme.palette.grey["200"],
        }
    },
    infoMessage: {
        ...theme.typography.infoMessage,
    },
}));

const GuestsCountDialog = ({
    open,
    onClose,
    onApplyChanges,
    maxGuests,
    numberOfGuests,
    wheelChair,
    isWheelchairAccessibilityEnabled
}) => {
    const classes = useStyles();
    const {i18n} = useI18N();

    const [guestsCount, setGuestsCount] = useState(numberOfGuests);
    const [wheelChairValue, setWheelChairValue] = useState(wheelChair);

    const changeNumberOfGuests = (increment) => {
        setGuestsCount((prev) => Math.max(1, prev + increment));
    };

    const handleChangeWheelChair = () => {
        setWheelChairValue(!wheelChairValue);
    };

    const handleChangeGuests = () => {
        onApplyChanges({guestsCount, wheelChairValue});
    };

    return (
        <NoIndexDialog
            open={open}
            fullScreen={false}
            maxWidth="md"
            onClose={onClose}
            PaperProps={{
                className: classes.dialog,
            }}
        >
            <DialogTitle>
                <Box
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                >
                    <Box pt={3}>
                        <Typography
                            variant="subtitle1"
                            align="center"
                        >
                            {i18n("dialog.search_bar.search_date_guest.guests")}
                        </Typography>

                        <Typography
                            variant="body2"
                            align="center"
                        >
                            {i18n("view.search_bar.search_guests.infants")}
                        </Typography>
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        pt={5}
                        pb={3}
                    >
                        <Box>
                            <IconButton
                                color={"primary"}
                                disabled={guestsCount === 1}
                                onClick={() => changeNumberOfGuests(-1)}
                            >
                                <StreamlineIcon
                                    className={clsx(classes.removeAddIcon, {["disabled"]: guestsCount === 1})}
                                >
                                    <SubtractCircle/>
                                </StreamlineIcon>
                            </IconButton>

                        </Box>

                        <Box>
                            <Box
                                px={1.5}
                            >
                                <Typography
                                    className={classes.infoMessage}
                                >
                                    {guestsCount}
                                </Typography>
                            </Box>
                        </Box>

                        <Box>
                            <IconButton
                                disabled={guestsCount === maxGuests}
                                onClick={() => changeNumberOfGuests(1)}
                            >
                                <StreamlineIcon
                                    className={clsx(classes.removeAddIcon, {["disabled"]: guestsCount === maxGuests})}
                                >
                                    <AddCircle/>
                                </StreamlineIcon>
                            </IconButton>
                        </Box>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Box
                    display={"flex"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    flexWrap={"nowrap"}
                    width={"100%"}
                >
                    <Typography
                        variant="h6"
                        align="center"
                    >
                        {isWheelchairAccessibilityEnabled ?
                            i18n("view.search_bar.search_guests.wheelchair") : i18n("view.search_bar.search_guests.no_wheelchair_accessibility")
                        }
                    </Typography>
                    {isWheelchairAccessibilityEnabled && (
                        <Switch
                            color="primary"
                            checked={wheelChairValue}
                            onChange={handleChangeWheelChair}
                        />
                    )}
                </Box>
            </DialogContent>

            <DialogActions>
                <CustomButton
                    variant="contained"
                    disableElevation
                    size={"medium"}
                    onClick={handleChangeGuests}
                >
                    {i18n("common.label.apply")}
                </CustomButton>
            </DialogActions>
        </NoIndexDialog>
    );
};

GuestsCountDialog.defaultProps = {
    numberOfGuests: 2,
    wheelChair: false,
};

GuestsCountDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onApplyChanges: PropTypes.func,
    maxGuests: PropTypes.number,
    numberOfGuests: PropTypes.number,
    wheelChair: PropTypes.bool,
    isWheelchairAccessibilityEnabled: PropTypes.bool,
};

export default GuestsCountDialog;
